import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { translations } from '../translations';
import { AiOutlineCopyrightCircle  } from "react-icons/ai";
import { config } from '../config';

axios.defaults.baseURL = config.API_URL;

const TermsOfService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [displayLanguage, setDisplayLanguage] = useState("eng");
  const [oemPartnerData, setOemPartnerData] = useState(null);
  
  useEffect(() => {
    const savedLanguage = localStorage.getItem('displayLanguage') || 'eng';
    setDisplayLanguage(savedLanguage);
    
    const searchParams = new URLSearchParams(location.search);
    const oemId = searchParams.get('oemId');
    
    if (oemId) {
      const fetchOemData = async () => {
        try {
          const response = await axios.post('/partners/oem-partners/check-url-status', { oemId });
          if (response.data.status === 200) {
            const decodedLogo = response.data.logo?.replace(/&quot;/g, '"').replace(/&amp;/g, '&') || '';
            setOemPartnerData({
              status: response.data.status,
              response: response.data.response,
              oemPartner: response.data.oemPartner,
              logo: decodedLogo
            });
          }
        } catch (error) {
          console.error('Error fetching OEM data:', error);
        }
      };
      fetchOemData();
    }
  }, [location.search]);

  const currentLang = displayLanguage === "ja" ? "ja" : "eng";
  const t = translations[currentLang];

  const handleBackToHome = () => {
    const searchParams = new URLSearchParams(location.search);
    navigate(`/cyberRiskReport${searchParams.toString() ? '?' + searchParams.toString() : ''}`);
  };

  const decodeHtmlEntities = (str) => {
    if (!str) return str;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  };

  return (
    <div style={styles.container}>
      {/* Language Toggle Button - Fixed Position */}
      <div className="language-toggle-container" style={{ 
        position: "fixed", 
        top: "20px", 
        right: "20px", 
        zIndex: 1000 
      }}>
        <div className="language-toggle" role="group" aria-label="Language selection">
          <button
            type="button"
            className={`language-toggle-btn ${displayLanguage === "eng" ? "active" : ""}`}
            onClick={() => {
              setDisplayLanguage("eng");
              localStorage.setItem('displayLanguage', 'eng');
            }}
            aria-pressed={displayLanguage === "eng"}
            aria-label={t.switchToEnglish}
          >
            <span className="flag-icon">🇺🇸</span>
            <span className="language-text">English</span>
          </button>
          <button
            type="button"
            className={`language-toggle-btn ${displayLanguage === "ja" ? "active" : ""}`}
            onClick={() => {
              setDisplayLanguage("ja");
              localStorage.setItem('displayLanguage', 'ja');
            }}
            aria-pressed={displayLanguage === "ja"}
            aria-label={t.switchToJapanese}
          >
            <span className="flag-icon">🇯🇵</span>
            <span className="language-text">Japanese</span>
          </button>
        </div>
      </div>

      {/* Header with Logo */}
      <div style={styles.header}>
        {oemPartnerData?.logo && (
          <div style={styles.logoContainer}>
            <img 
              src={oemPartnerData.logo}
              alt={oemPartnerData.oemPartner || 'OEM Partner'} 
              style={styles.logo} 
              onError={(e) => console.log('Image failed to load:', oemPartnerData.logo)}
            />
          </div>
        )}
        <h1 style={styles.title}>{t.termsOfService}</h1>
        <div style={styles.subtitle}>{t.cyberRiskReport}</div>
        {oemPartnerData?.oemPartner && (
          <div style={styles.partnerInfo}>
            {t.poweredBy || 'Powered by'} <strong>{oemPartnerData.oemPartner}</strong>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* Section 1: Overview */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>1</div>
            <h2 style={styles.sectionTitle}>{t.overview}</h2>
          </div>
          <p style={styles.text}>{t.overviewText1}</p>
          <p style={styles.text}>{t.overviewText2}</p>
          <p style={styles.text}>{t.overviewText3}</p>
        </div>

        {/* Section 2: Diagnostic Target */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>2</div>
            <h2 style={styles.sectionTitle}>{t.diagnosticTarget}</h2>
          </div>
          <h3 style={styles.subsectionTitle}>{t.singleDomainScope}</h3>
          <p style={styles.text}>{t.diagnosticText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.diagnosticItem1}</li>
            <li style={styles.listItem}>{t.diagnosticItem2}</li>
          </ul>
          <p style={styles.text}>{t.diagnosticText2}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.diagnosticNotItem1}</li>
            <li style={styles.listItem}>{t.diagnosticNotItem2}</li>
            <li style={styles.listItem}>{t.diagnosticNotItem3}</li>
            <li style={styles.listItem}>{t.diagnosticNotItem4}</li>
          </ul>
          <p style={styles.text}>{t.diagnosticText3}</p>
        </div>

        {/* Section 3: Subdomains */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>3</div>
            <h2 style={styles.sectionTitle}>{t.subdomains}</h2>
          </div>
          <p style={styles.text}>{t.subdomainsText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.subdomainsItem1}</li>
            <li style={styles.listItem}>{t.subdomainsItem2}</li>
          </ul>
          <p style={styles.text}>{t.subdomainsText2}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.subdomainsNotItem1}</li>
            <li style={styles.listItem}>{t.subdomainsNotItem2}</li>
          </ul>
          <p style={styles.text}>{t.subdomainsText3}</p>
        </div>

        {/* Section 4: Assessment Methodology */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>4</div>
            <h2 style={styles.sectionTitle}>{t.assessmentMethodology}</h2>
          </div>
          <p style={styles.text}>{t.methodologyText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.methodologyItem1}</li>
            <li style={styles.listItem}>{t.methodologyItem2}</li>
            <li style={styles.listItem}>{t.methodologyItem3}</li>
            <li style={styles.listItem}>{t.methodologyItem4}</li>
            <li style={styles.listItem}>{t.methodologyItem5}</li>
            <li style={styles.listItem}>{t.methodologyItem6}</li>
            <li style={styles.listItem}>{t.methodologyItem7}</li>
            <li style={styles.listItem}>{t.methodologyItem8}</li>
            <li style={styles.listItem}>{t.methodologyItem9}</li>
            <li style={styles.listItem}>{t.methodologyItem10}</li>
          </ul>
          <p style={styles.text}>{t.methodologyText2}</p>
          <p style={styles.text}>{t.methodologyText3}</p>
        </div>

        {/* Section 5: Risk Scoring */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>5</div>
            <h2 style={styles.sectionTitle}>{t.riskScoring}</h2>
          </div>
          <p style={styles.text}>{t.riskScoringText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.riskScoringItem1}</li>
            <li style={styles.listItem}>{t.riskScoringItem2}</li>
            <li style={styles.listItem}>{t.riskScoringItem3}</li>
            <li style={styles.listItem}>{t.riskScoringItem4}</li>
            <li style={styles.listItem}>{t.riskScoringItem5}</li>
            <li style={styles.listItem}>{t.riskScoringItem6}</li>
          </ul>
          <p style={styles.text}>{t.riskScoringText2}</p>
          <p style={styles.text}>{t.riskScoringText3}</p>
        </div>

        {/* Section 6: What the Service Does Not Include */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>6</div>
            <h2 style={styles.sectionTitle}>{t.serviceNotInclude}</h2>
          </div>
          <p style={styles.text}>{t.notIncludeText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.notIncludeItem1}</li>
            <li style={styles.listItem}>{t.notIncludeItem2}</li>
            <li style={styles.listItem}>{t.notIncludeItem3}</li>
            <li style={styles.listItem}>{t.notIncludeItem4}</li>
            <li style={styles.listItem}>{t.notIncludeItem5}</li>
            <li style={styles.listItem}>{t.notIncludeItem6}</li>
            <li style={styles.listItem}>{t.notIncludeItem7}</li>
            <li style={styles.listItem}>{t.notIncludeItem8}</li>
            <li style={styles.listItem}>{t.notIncludeItem9}</li>
          </ul>
          <p style={styles.text}>{t.notIncludeText2}</p>
        </div>

        {/* Section 7: Data Sources */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>7</div>
            <h2 style={styles.sectionTitle}>{t.dataSources}</h2>
          </div>
          <p style={styles.text}>{t.dataSourcesText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.dataSourcesItem1}</li>
            <li style={styles.listItem}>{t.dataSourcesItem2}</li>
            <li style={styles.listItem}>{t.dataSourcesItem3}</li>
            <li style={styles.listItem}>{t.dataSourcesItem4}</li>
            <li style={styles.listItem}>{t.dataSourcesItem5}</li>
            <li style={styles.listItem}>{t.dataSourcesItem6}</li>
          </ul>
          <p style={styles.text}>{t.dataSourcesText2}</p>
        </div>

        {/* Section 8: Deliverables */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>8</div>
            <h2 style={styles.sectionTitle}>{t.deliverables}</h2>
          </div>
          <p style={styles.text}>{t.deliverablesText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.deliverablesItem1}</li>
            <li style={styles.listItem}>{t.deliverablesItem2}</li>
            <li style={styles.listItem}>{t.deliverablesItem3}</li>
            <li style={styles.listItem}>{t.deliverablesItem4}</li>
          </ul>
          <p style={styles.text}>{t.deliverablesText2}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.deliverablesItem5}</li>
            <li style={styles.listItem}>{t.deliverablesItem6}</li>
            <li style={styles.listItem}>{t.deliverablesItem7}</li>
            <li style={styles.listItem}>{t.deliverablesItem8}</li>
            <li style={styles.listItem}>{t.deliverablesItem9}</li>
          </ul>
        </div>

        {/* Section 9: Customer Responsibilities */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>9</div>
            <h2 style={styles.sectionTitle}>{t.customerResponsibilities}</h2>
          </div>
          <p style={styles.text}>{t.customerRespText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.customerRespItem1}</li>
            <li style={styles.listItem}>{t.customerRespItem2}</li>
            <li style={styles.listItem}>{t.customerRespItem3}</li>
            <li style={styles.listItem}>{t.customerRespItem4}</li>
          </ul>
          <p style={styles.text}>{t.customerRespText2}</p>
        </div>

        {/* Section 10: Service Limitations */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>10</div>
            <h2 style={styles.sectionTitle}>{t.serviceLimitations}</h2>
          </div>
          <p style={styles.text}>{t.limitationsText1}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.limitationsItem1}</li>
            <li style={styles.listItem}>{t.limitationsItem2}</li>
            <li style={styles.listItem}>{t.limitationsItem3}</li>
            <li style={styles.listItem}>{t.limitationsItem4}</li>
          </ul>
          <p style={styles.text}>{t.limitationsText2}</p>
        </div>

        {/* Section 11: Service Availability */}
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionNumber}>11</div>
            <h2 style={styles.sectionTitle}>{t.serviceAvailability}</h2>
          </div>
          <p style={styles.text}>{t.availabilityText1}</p>
          <p style={styles.text}>{t.availabilityText2}</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>{t.availabilityItem1}</li>
            <li style={styles.listItem}>{t.availabilityItem2}</li>
            <li style={styles.listItem}>{t.availabilityItem3}</li>
          </ul>
          <p style={styles.text}>{t.availabilityText3}</p>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.footerContent}>
          <button onClick={handleBackToHome} style={styles.backButton}>← {t.backToHome}</button>
          <div style={styles.copyright}> <span> <AiOutlineCopyrightCircle  size={14} style={{marginBottom:"2px"}} /></span> {new Date().getUTCFullYear()} {t.copyrightText}</div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    backgroundColor: '#ffffff',
    padding: '24px 0',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    textAlign: 'center',
    borderBottom: '1px solid #e2e8f0'
  },
  logoContainer: {
    marginBottom: '16px'
  },
  logo: {
    filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.3))',
    width: '130px',
    height: '80px',
    objectFit: 'contain'
  },
  partnerInfo: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
    marginTop: '8px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    margin: '8px 0 0 0',
    background: 'linear-gradient(90deg, #1e40af, #2563eb)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline-block'
  },
  subtitle: {
    fontSize: '16px',
    color: '#64748b',
    fontWeight: '500',
    marginTop: '4px'
  },
  content: {
    flex: 1,
    padding: '32px 24px',
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto'
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px'
  },
  sectionNumber: {
    backgroundColor: '#2563eb',
    color: 'white',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '12px',
    fontWeight: '600',
    fontSize: '14px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0
  },
  text: {
    fontSize: '15px',
    color: '#475569',
    lineHeight: '1.6',
    margin: 0,
    marginBottom: '12px'
  },
  subsectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#334155',
    marginTop: '8px',
    marginBottom: '12px'
  },
  list: {
    margin: '12px 0',
    paddingLeft: '24px'
  },
  listItem: {
    fontSize: '15px',
    color: '#475569',
    lineHeight: '1.6',
    marginBottom: '8px'
  },
  footer: {
    backgroundColor: '#ffffff',
    padding: '20px 24px',
    borderTop: '1px solid #e2e8f0',
    marginTop: '32px'
  },
  footerContent: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  backButton: {
    color: '#2563eb',
    background: 'none',
    border: 'none',
    fontWeight: '500',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'color 0.2s',
    padding: 0,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    ':hover': {
      color: '#1e40af'
    }
  },
  copyright: {
    color: '#64748b',
    fontSize: '13px'
  }
};

export default TermsOfService;
